import { useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { AppShell } from "./components/shell/AppShell";
import { openDb, closeDb } from "./db/dexie";
import { BoardScreen } from "./screens/BoardScreen";
import { CheckScreen } from "./screens/CheckScreen";
import { InviteScreen } from "./screens/InviteScreen";
import { LoadDetailScreen } from "./screens/LoadDetailScreen";
import { LoadsScreen } from "./screens/LoadsScreen";
import { LogsDetentionScreen } from "./screens/LogsDetentionScreen";
import { LogsFuelScreen } from "./screens/LogsFuelScreen";
import { LogsMaintenanceScreen } from "./screens/LogsMaintenanceScreen";
import { MoneyScreen } from "./screens/MoneyScreen";
import { SettlementScreen } from "./screens/SettlementScreen";
import { SettingsCosts } from "./screens/SettingsCosts";
import { SettingsImport } from "./screens/SettingsImport";
import { SettingsPay } from "./screens/SettingsPay";
import { SettingsPlan } from "./screens/SettingsPlan";
import { SettingsScreen } from "./screens/SettingsScreen";
import { SettingsSync } from "./screens/SettingsSync";
import { SettingsTeam } from "./screens/SettingsTeam";
import { SettingsTruck } from "./screens/SettingsTruck";
import { UpdateBanner } from "./components/shell/UpdateBanner";
import { SignInScreen } from "./screens/SignInScreen";
import { useAuthStore } from "./store/authStore";
import { applyStoredTheme } from "./store/themeStore";
import { getSyncClient, resetSyncClient } from "./sync/SyncClient";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const me = useAuthStore((s) => s.me);
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  const connection = useAuthStore((s) => s.connection);
  const loading = useAuthStore((s) => s.loading);
  const refreshMe = useAuthStore((s) => s.refreshMe);
  const location = useLocation();
  if (!bootstrapped) {
    if (connection === "unreachable" || connection === "offline") {
      return (
        <div className="auth-wrap" role="alert" data-testid="server-unreachable">
          <p>Can&apos;t reach the server.</p>
          <p className="muted">
            {connection === "offline"
              ? "This phone is offline. Connect, then retry."
              : "It didn't answer for over a minute. Check your connection, then retry."}
          </p>
          <button type="button" className="btn btn-primary" disabled={loading} onClick={() => void refreshMe()}>
            Retry
          </button>
        </div>
      );
    }
    return (
      <div className="auth-wrap" role="status" aria-live="polite" data-testid="server-waking">
        <p className="muted">Waking up the server… (first load after a break takes up to a minute)</p>
      </div>
    );
  }
  if (!me) {
    return <Navigate to="/signin" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}

function CheckRoute() {
  const { loadId } = useParams();
  return <CheckScreen key={loadId ?? "new"} />;
}

function LoadDetailRoute() {
  const { loadId } = useParams();
  // A9: remount when URL loadId changes so no stale contact/note/pay linger.
  return <LoadDetailScreen key={loadId} />;
}

export function App() {
  const me = useAuthStore((s) => s.me);
  const refreshMe = useAuthStore((s) => s.refreshMe);

  useEffect(() => {
    applyStoredTheme();
    void refreshMe();
  }, [refreshMe]);

  // Open Dexie synchronously when session is known so live queries never race.
  if (me) {
    openDb(me.user.id);
  }

  useEffect(() => {
    if (!me) {
      resetSyncClient();
      closeDb();
      return;
    }
    openDb(me.user.id);
    const client = getSyncClient();
    client.start();
    return () => client.stop();
  }, [me?.user.id]);

  return (
    <>
    <UpdateBanner />
    <Routes>
      <Route path="/signin" element={<SignInScreen />} />
      <Route path="/invite/:code" element={<InviteScreen />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Navigate to="/check" replace />} />
        <Route path="/check" element={<CheckRoute />} />
        <Route path="/check/:loadId" element={<CheckRoute />} />
        <Route path="/loads" element={<LoadsScreen />} />
        <Route path="/loads/:loadId" element={<LoadDetailRoute />} />
        <Route path="/logs/fuel" element={<LogsFuelScreen />} />
        <Route path="/logs/detention" element={<LogsDetentionScreen />} />
        <Route path="/logs/maintenance" element={<LogsMaintenanceScreen />} />
        <Route path="/money" element={<MoneyScreen />} />
        <Route path="/money/settlements/:id" element={<SettlementScreen />} />
        <Route path="/board" element={<BoardScreen />} />
        <Route path="/settings" element={<SettingsScreen />} />
        <Route path="/settings/truck" element={<SettingsTruck />} />
        <Route path="/settings/pay" element={<SettingsPay />} />
        <Route path="/settings/costs" element={<SettingsCosts />} />
        <Route path="/settings/team" element={<SettingsTeam />} />
        <Route path="/settings/plan" element={<SettingsPlan />} />
        <Route path="/settings/import" element={<SettingsImport />} />
        <Route path="/settings/sync" element={<SettingsSync />} />
      </Route>
      <Route path="*" element={<Navigate to="/check" replace />} />
    </Routes>
    </>
  );
}
